// File: AccountingVendorsManagement.tsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { 
  FaPlus, FaEdit, FaTrash, FaUserTie, FaEnvelope, 
  FaPhone, FaMapMarkerAlt, FaBuilding, FaIdCard 
} from 'react-icons/fa';
import { 
  getVendors, 
  createVendor, 
  updateVendor,
  deleteVendor,
  Vendor 
} from '../../../firebase/accountingFirestoreService';
import { useUI } from '../../../components/common/UIMessageProvider';

const AccountingVendorsManagement: React.FC = () => {
  const auth = useAuth();
  const canViewAccounting = auth.canPerformAction('accounting', 'view');
  const canAddAccounting = auth.canPerformAction('accounting', 'create');
  const canEditAccounting = auth.canPerformAction('accounting', 'edit');
  const canDeleteAccounting = auth.canPerformAction('accounting', 'delete');
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<Partial<Vendor>>({
    name: '',
    contactPerson: '',
    email: '',
    phone: '',
    address: '',
    taxId: '',
    category: '',
    paymentTerms: '',
    notes: ''
  });
  const ui = useUI();

  // Fetch vendors on mount
  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    setLoading(true);
    try {
      const result = await getVendors();
      if (result.success) {
        setVendors(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name?.trim()) {
      ui.showMessage('Vendor name is required', { type: 'warning' });
      return;
    }

    try {
      if (editingVendor?.id) {
        // Update existing
        const result = await updateVendor(editingVendor.id, formData);
        if (result.success) {
          setVendors(prev => prev.map(v => 
            v.id === editingVendor.id ? { ...v, ...formData } : v
          ));
        }
      } else {
        // Create new
        const result = await createVendor(formData as Vendor);
        if (result.success) {
          fetchVendors(); // Refresh list
        }
      }
      
      // Reset form
      setFormData({
        name: '', contactPerson: '', email: '', phone: '',
        address: '', taxId: '', category: '', paymentTerms: '', notes: ''
      });
      setEditingVendor(null);
      setShowForm(false);
    } catch (error) {
      console.error('Error saving vendor:', error);
    }
  };

  const handleEdit = (vendor: Vendor) => {
    setEditingVendor(vendor);
    setFormData(vendor);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    const ok = await ui.askConfirm({
      title: 'Delete Vendor',
      message: 'Are you sure you want to delete this vendor?',
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });
    if (!ok) return;
    try {
      const result = await deleteVendor(id);
      if (result.success) {
        setVendors(prev => prev.filter(v => v.id !== id));
        ui.showMessage('Vendor deleted', { type: 'success' });
      } else {
        ui.showMessage('Failed to delete vendor', { type: 'error' });
      }
    } catch (err) {
      console.error('Error deleting vendor:', err);
      ui.showMessage('Failed to delete vendor', { type: 'error' });
    }
  };

  const vendorCategories = [
    'Supplier', 'Service Provider', 'Consultant', 'Contractor',
    'Utility Company', 'Government', 'Retailer', 'Wholesaler',
    'Manufacturer', 'Distributor', 'Other'
  ];

  const paymentTermsOptions = [
    'Net 30', 'Net 60', 'Net 90', 'Due on Receipt',
    '50% Advance, 50% on Completion', 'Monthly', 'Quarterly','None'
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-soft p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Vendors</h1>
            <p className="text-sm text-gray-600">Manage suppliers & service providers</p>
          </div>
          {canAddAccounting && (
            <button
              onClick={() => {
                setEditingVendor(null);
                setFormData({ name: '', contactPerson: '', email: '', phone: '', address: '', taxId: '', category: '', paymentTerms: '', notes: '' });
                setShowForm(true);
              }}
              className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-3 py-1.5 rounded-md text-sm font-medium transition-colors"
            >
              <FaPlus className="h-4 w-4" />
              Add
            </button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-600">Total</p>
                <p className="text-lg font-semibold text-gray-900">{vendors.length}</p>
              </div>
              <FaBuilding className="h-6 w-6 text-blue-500" />
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-green-50 to-green-100 border border-green-200 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-green-600">With Contact</p>
                <p className="text-lg font-semibold text-gray-900">{vendors.filter(v => v.contactPerson).length}</p>
              </div>
              <FaUserTie className="h-6 w-6 text-green-500" />
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-purple-50 to-purple-100 border border-purple-200 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-purple-600">With Email</p>
                <p className="text-lg font-semibold text-gray-900">{vendors.filter(v => v.email).length}</p>
              </div>
              <FaEnvelope className="h-6 w-6 text-purple-500" />
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-orange-50 to-orange-100 border border-orange-200 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-orange-600">Tax</p>
                <p className="text-lg font-semibold text-gray-900">{vendors.filter(v => v.taxId).length}</p>
              </div>
              <FaIdCard className="h-6 w-6 text-orange-500" />
            </div>
          </div>
        </div>

        {/* Add/Edit Form */}
        {showForm && (
          <div className="mb-4 bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h3 className="text-base font-medium text-gray-900 mb-3">{editingVendor ? 'Edit Vendor' : 'Add Vendor'}</h3>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vendor *</label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                      <FaBuilding className="h-4 w-4 text-gray-400" />
                    </div>
                    <input type="text" value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md text-sm" placeholder="Vendor name" required />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select value={formData.category} onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
                    <option value="">Select</option>
                    {vendorCategories.map(category => <option key={category} value={category}>{category}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact</label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2"><FaUserTie className="h-4 w-4 text-gray-400" /></div>
                    <input type="text" value={formData.contactPerson} onChange={(e) => setFormData(prev => ({ ...prev, contactPerson: e.target.value }))} className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md text-sm" placeholder="Contact person" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2"><FaEnvelope className="h-4 w-4 text-gray-400" /></div>
                    <input type="email" value={formData.email} onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))} className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md text-sm" placeholder="email@example.com" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2"><FaPhone className="h-4 w-4 text-gray-400" /></div>
                    <input type="tel" value={formData.phone} onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))} className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md text-sm" placeholder="+123456789" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Terms</label>
                  <select value={formData.paymentTerms} onChange={(e) => setFormData(prev => ({ ...prev, paymentTerms: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
                    <option value="">Select</option>
                    {paymentTermsOptions.map(term => <option key={term} value={term}>{term}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <div className="relative">
                  <div className="absolute left-3 top-3"><FaMapMarkerAlt className="h-4 w-4 text-gray-400" /></div>
                  <textarea value={formData.address} onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))} rows={2} className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md text-sm" placeholder="Address" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tax ID</label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2"><FaIdCard className="h-4 w-4 text-gray-400" /></div>
                    <input type="text" value={formData.taxId} onChange={(e) => setFormData(prev => ({ ...prev, taxId: e.target.value }))} className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md text-sm" placeholder="TAX-123" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea value={formData.notes} onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" placeholder="Notes" />
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t">
                <button type="button" onClick={() => { setShowForm(false); setEditingVendor(null); }} className="px-4 py-1.5 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm">Cancel</button>
                <button type="submit" className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-4 py-1.5 rounded-md text-sm"><FaPlus className="h-4 w-4" />{editingVendor ? 'Update' : 'Save'}</button>
              </div>
            </form>
          </div>
        )}

        {/* Vendors List */}
        <div className="bg-white rounded-xl shadow-soft overflow-hidden border border-gray-100">
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center h-40">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
              </div>
            ) : vendors.length === 0 ? (
              <div className="text-center py-8">
                <FaBuilding className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                <h3 className="text-base font-medium text-gray-900 mb-1">No vendors yet</h3>
                <p className="text-sm text-gray-600 mb-3">Create your first vendor</p>
                {canAddAccounting && (
                  <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-3 py-1.5 rounded-md text-sm">
                    <FaPlus className="h-4 w-4" />
                    Add
                  </button>
                )}
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendor</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {vendors.map((vendor) => (
                    <tr key={vendor.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-md bg-blue-100 flex items-center justify-center">
                            <FaBuilding className="h-3 w-3 text-blue-600" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{vendor.name}</div>
                            {vendor.email && <div className="text-xs text-gray-500">{vendor.email}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="space-y-0.5">
                          {vendor.contactPerson && <div className="text-sm text-gray-900">{vendor.contactPerson}</div>}
                          {vendor.phone && <div className="text-xs text-gray-500">{vendor.phone}</div>}
                        </div>
                      </td>
                      <td className="px-3 py-2">{vendor.category ? <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">{vendor.category}</span> : <span className="text-sm text-gray-400">-</span>}</td>
                      <td className="px-3 py-2 text-sm text-gray-500">{vendor.paymentTerms || '-'}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          {canEditAccounting && (
                            <button onClick={() => handleEdit(vendor)} className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50" title="Edit"><FaEdit className="h-4 w-4" /></button>
                          )}
                          {canDeleteAccounting && (
                            <button onClick={() => vendor.id && handleDelete(vendor.id)} className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50" title="Delete"><FaTrash className="h-4 w-4" /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountingVendorsManagement;